// src/pages/B2B/event-reports/pdf/warm/buildWarmPdf.ts
import React from 'react';
import { createRoot } from 'react-dom/client';
import { flushSync } from 'react-dom';
import { WarmReportLayout, type WarmReportLayoutProps } from './WarmReportLayout';

function awaitImg(img: HTMLImageElement): Promise<void> {
  if (img.complete && img.naturalWidth > 0) return Promise.resolve();
  return new Promise((resolve) => {
    img.addEventListener('load', () => resolve(), { once: true });
    img.addEventListener('error', () => resolve(), { once: true });
  });
}

export async function buildWarmPdfBlob(input: WarmReportLayoutProps): Promise<Blob> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const html2pdfModule: any = await import('html2pdf.js');
  // CRA's CJS interop typically exposes the function as .default; some bundler
  // configs surface it directly. Try both, then fall back to a deeper unwrap.
  const html2pdf =
    (typeof html2pdfModule === 'function' && html2pdfModule) ||
    (typeof html2pdfModule.default === 'function' && html2pdfModule.default) ||
    (typeof html2pdfModule.default?.default === 'function' && html2pdfModule.default.default);
  if (!html2pdf) {
    throw new Error('html2pdf.js did not resolve to a callable');
  }

  const root = document.createElement('div');
  // Mount on-screen but invisible to the user — html2canvas needs the element
  // in flow with non-zero dimensions. `opacity:0; pointer-events:none` keeps
  // it captured cleanly while not affecting the user's view.
  root.style.cssText =
    'position:fixed;left:0;top:0;width:816px;background:#fdf6f3;z-index:-1;opacity:0;pointer-events:none';
  document.body.appendChild(root);
  const reactRoot = createRoot(root);

  // Force synchronous commit so the DOM is fully populated before snapshot.
  // React 19's concurrent render won't otherwise flush by the next animation
  // frame and html2canvas captures a half-empty tree.
  flushSync(() => {
    reactRoot.render(React.createElement(WarmReportLayout, input));
  });

  try {
    if (document.fonts) await document.fonts.ready;
    await Promise.all(
      Array.from(root.querySelectorAll('img')).map((img) => awaitImg(img as HTMLImageElement)),
    );
    // Two RAFs + a small settle. SVG <text> measurement and gradient layout
    // sometimes need an extra layout pass.
    await new Promise((r) => requestAnimationFrame(r));
    await new Promise((r) => requestAnimationFrame(r));
    await new Promise((r) => setTimeout(r, 100));

    const blob: Blob = await html2pdf()
      .from(root)
      .set({
        margin: [8, 8, 10, 8],
        filename: 'report.pdf',
        image: { type: 'jpeg', quality: 0.95 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          backgroundColor: '#fdf6f3',
          windowWidth: 816,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onclone: (cd: Document) => {
            // Make the cloned root visible inside the html2canvas iframe.
            cd.querySelectorAll('.warm-report').forEach((el: Element) => {
              const e = el as HTMLElement;
              e.style.opacity = '1';
              e.style.position = 'static';
            });
            // Replace gradient-clipped values with solid color (Safari + canvas
            // don't honor -webkit-background-clip:text and renders blank).
            cd.querySelectorAll('.warm-report .stat .val').forEach((el: Element) => {
              const e = el as HTMLElement;
              e.style.background = 'none';
              e.style.webkitTextFillColor = '#c41e3a';
              e.style.color = '#c41e3a';
            });
          },
        },
        jsPDF: { unit: 'in', format: 'letter' },
      })
      .outputPdf('blob');
    return blob;
  } finally {
    reactRoot.unmount();
    root.remove();
  }
}
